import { useState, useCallback, useRef } from 'react';
import { useWalletConnect } from '@btc-vision/walletconnect';
import { getContract, JSONRpcProvider, TransactionOutputFlags } from 'opnet';
import { networks, toBech32, toSatoshi } from '@btc-vision/bitcoin';
import { Campaign } from '../config/campaigns';
import {
    BITFUND_CONTRACT_ADDRESS,
    BITFUND_ABI,
    IBitFundContract,
} from '../config/contract';

export interface DonationResult {
    readonly txId: string;
    readonly campaignId: bigint;
    readonly amountBTC: number;
    readonly donorAddress: string;
}

interface DonateModalProps {
    readonly campaign: Campaign;
    readonly onClose: () => void;
    readonly onSuccess: (result: DonationResult) => void;
}

const PRESETS = [0.001, 0.005, 0.01, 0.05];

/** Convert a 0x-prefixed u256 hex to a bech32m taproot address for the given network. */
function u256HexToAddress(hex: string, network: typeof networks.opnetTestnet): string {
    const raw = hex.startsWith('0x') ? hex.slice(2) : hex;
    const bytes = new Uint8Array(32);
    for (let i = 0; i < 32; i++) {
        bytes[i] = parseInt(raw.slice(i * 2, i * 2 + 2), 16);
    }
    return toBech32(bytes, 1, network.bech32);
}


/**
 * Converts a BTC string amount to satoshis using string parsing.
 * Avoids float multiplication which loses precision
 * (e.g. 0.29 * 1e8 = 28999999.999999996).
 */
function btcStringToSats(btcStr: string): bigint {
    const trimmed = btcStr.trim();
    if (!trimmed || trimmed === '0') return 0n;

    const dotIndex = trimmed.indexOf('.');
    if (dotIndex === -1) {
        return BigInt(trimmed) * 100_000_000n;
    }

    const whole = trimmed.slice(0, dotIndex) || '0';
    const frac = trimmed.slice(dotIndex + 1).padEnd(8, '0').slice(0, 8);
    return BigInt(whole) * 100_000_000n + BigInt(frac);
}

export function DonateModal({ campaign, onClose, onSuccess }: DonateModalProps) {
    const { walletAddress, openConnectModal, provider, publicKey, address } =
        useWalletConnect();
    const [amount, setAmount] = useState('');
    const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
    const [txHash, setTxHash] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [linkCopied, setLinkCopied] = useState(false);
    const submittingRef = useRef(false);

    const handleCopyLink = () => {
        if (campaign.isExample) return;
        const url = `${window.location.origin}${window.location.pathname}?campaign=${campaign.id}`;
        navigator.clipboard.writeText(url).then(() => {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        });
    };

    const progress =
        campaign.goalBTC > 0
            ? Math.min((campaign.raisedBTC / campaign.goalBTC) * 100, 100)
            : 0;

    const handleDonate = useCallback(async () => {
        if (submittingRef.current) return;

        if (!walletAddress || !provider || !publicKey || !address) {
            openConnectModal();
            return;
        }

        const btcAmount = parseFloat(amount);
        if (isNaN(btcAmount) || btcAmount <= 0) return;

        if (campaign.isExample) {
            setErrorMsg(
                'This is an example campaign. Create your own to receive real donations!',
            );
            setStatus('error');
            return;
        }

        submittingRef.current = true;
        setStatus('sending');
        setErrorMsg('');

        try {
            const rpcProvider = provider as JSONRpcProvider;
            const network = networks.opnetTestnet;

            const donationSats = btcStringToSats(amount);
            if (donationSats <= 0n) {
                throw new Error('Invalid donation amount');
            }

            const contract = getContract<IBitFundContract>(
                BITFUND_CONTRACT_ADDRESS,
                BITFUND_ABI,
                rpcProvider,
                network,
                address,
            );

            // Convert creator u256 hex to bech32 taproot address for the BTC output
            const creatorBtcAddr = u256HexToAddress(campaign.creatorAddress, network);

            // Set transaction details so the contract can verify the BTC output during simulation
            // (follows Verify-Don't-Custody pattern from OPNet GoFundMe guide)
            contract.setTransactionDetails({
                inputs: [],
                outputs: [
                    {
                        to: creatorBtcAddr,
                        value: donationSats,
                        index: 1,
                        scriptPubKey: undefined,
                        flags: TransactionOutputFlags.hasTo,
                    },
                ],
            });

            // Simulate donate(campaignId, amount)
            const sim = await contract.donate(campaign.id, donationSats);
            if ('error' in sim && sim.error) {
                throw new Error(String(sim.error));
            }

            // Send — signer: null on frontend (wallet handles signing)
            const receipt = await sim.sendTransaction({
                signer: null,
                mldsaSigner: null,
                refundTo: walletAddress,
                maximumAllowedSatToSpend: donationSats + 10_000n,
                network,
                extraOutputs: [
                    {
                        address: creatorBtcAddr,
                        value: toSatoshi(donationSats),
                    },
                ],
            });

            if (receipt.transactionId) {
                setTxHash(receipt.transactionId);
                setStatus('success');
            } else {
                throw new Error('Transaction returned no result');
            }
        } catch (err: unknown) {
            console.error('Donation failed:', err);
            const message = err instanceof Error ? err.message : 'Transaction failed';
            setErrorMsg(message);
            setStatus('error');
        } finally {
            submittingRef.current = false;
        }
    }, [walletAddress, provider, publicKey, address, openConnectModal, amount, campaign]);

    const handleDone = () => {
        onSuccess({
            txId: txHash,
            campaignId: campaign.id,
            amountBTC: parseFloat(amount),
            donorAddress: walletAddress ?? '',
        });
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal">
                <div className="modal__header">
                    <h2 className="modal__title">
                        {status === 'success' ? 'Thank You!' : 'Fund This Campaign'}
                    </h2>
                    <button className="modal__close" onClick={onClose} aria-label="Close">
                        x
                    </button>
                </div>

                {status === 'success' ? (
                    <div style={{ textAlign: 'center', padding: 'var(--space-xl) 0' }}>
                        <div
                            style={{
                                fontSize: '4rem',
                                marginBottom: 'var(--space-lg)',
                                animation: 'fade-in-up 0.5s ease-out',
                            }}
                        >
                            {'\u2705'}
                        </div>
                        <p
                            style={{
                                fontSize: 'var(--font-size-lg)',
                                color: 'var(--text-primary)',
                                marginBottom: 'var(--space-sm)',
                            }}
                        >
                            Donation sent on-chain!
                        </p>
                        <p
                            style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--text-secondary)',
                                marginBottom: 'var(--space-md)',
                            }}
                        >
                            {amount} BTC sent to &quot;{campaign.title}&quot;
                        </p>
                        {txHash && (
                            <div className="donate-success__tx">
                                TX:{' '}
                                <a
                                    className="tx-link"
                                    href={`https://mempool.opnet.org/testnet4/tx/${txHash}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {txHash.slice(0, 16)}...{txHash.slice(-8)}
                                </a>
                            </div>
                        )}
                        <button className="btn btn--primary btn--lg" onClick={handleDone}>
                            Done
                        </button>
                    </div>
                ) : (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-sm)' }}>
                            <p className="modal__campaign-name" style={{ marginBottom: 0 }}>
                                {campaign.icon} {campaign.title}
                            </p>
                            {!campaign.isExample && (
                                <button
                                    onClick={handleCopyLink}
                                    title="Copy campaign link"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '4px 10px',
                                        border: '1px solid var(--border-interactive)',
                                        borderRadius: 'var(--radius-sm)',
                                        background: 'transparent',
                                        color: linkCopied ? 'var(--color-success)' : 'var(--text-muted)',
                                        cursor: 'pointer',
                                        fontSize: 'var(--font-size-xs)',
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    {linkCopied ? (
                                        <>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            Copied
                                        </>
                                    ) : (
                                        <>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                                                <polyline points="16 6 12 2 8 6" />
                                                <line x1="12" y1="2" x2="12" y2="15" />
                                            </svg>
                                            Share
                                        </>
                                    )}
                                </button>
                            )}
                        </div>

                        <p
                            style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--text-secondary)',
                                lineHeight: 1.6,
                                marginBottom: 'var(--space-lg)',
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {campaign.description}
                        </p>

                        <div className="progress" style={{ marginBottom: 'var(--space-lg)' }}>
                            <div className="progress__bar-bg">
                                <div
                                    className="progress__bar-fill"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                            <div className="progress__info">
                                <span className="progress__amount">
                                    {campaign.raisedBTC.toFixed(4)} / {campaign.goalBTC} BTC
                                </span>
                                <span>{progress.toFixed(0)}%</span>
                            </div>
                        </div>

                        {campaign.creatorAddress && (
                            <div
                                className="modal__summary"
                                style={{ marginBottom: 'var(--space-lg)' }}
                            >
                                <span className="modal__summary-label">Creator</span>
                                <span
                                    className="modal__summary-value"
                                    style={{ fontSize: 'var(--font-size-xs)' }}
                                >
                                    {campaign.creatorAddress.slice(0, 12)}...
                                    {campaign.creatorAddress.slice(-6)}
                                </span>
                            </div>
                        )}

                        <div className="input-group">
                            <label className="input-group__label" htmlFor="donate-amount">
                                Amount (BTC)
                            </label>
                            <input
                                id="donate-amount"
                                className="input-group__field"
                                type="number"
                                step="0.001"
                                min="0.0001"
                                placeholder="0.01"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                disabled={status === 'sending'}
                            />
                            <div className="input-group__presets">
                                {PRESETS.map((preset) => (
                                    <button
                                        key={preset}
                                        className="input-group__preset"
                                        onClick={() => setAmount(preset.toString())}
                                        disabled={status === 'sending'}
                                    >
                                        {preset} BTC
                                    </button>
                                ))}
                            </div>
                        </div>

                        {amount && parseFloat(amount) > 0 && (
                            <div className="modal__summary">
                                <span className="modal__summary-label">You will send</span>
                                <span className="modal__summary-value">{amount} BTC</span>
                            </div>
                        )}

                        <button
                            className="btn btn--primary btn--lg"
                            style={{ width: '100%', marginTop: 'var(--space-md)' }}
                            onClick={walletAddress ? handleDonate : openConnectModal}
                            disabled={
                                status === 'sending' || !amount || parseFloat(amount) <= 0
                            }
                        >
                            {!walletAddress
                                ? 'Connect Wallet to Donate'
                                : status === 'sending'
                                    ? 'Confirm in OP_WALLET...'
                                    : `Donate ${amount || '0'} BTC`}
                        </button>

                        {status === 'error' && (
                            <p
                                style={{
                                    color: 'var(--color-error)',
                                    fontSize: 'var(--font-size-sm)',
                                    textAlign: 'center',
                                    marginTop: 'var(--space-md)',
                                }}
                            >
                                {errorMsg || 'Transaction failed. Please try again.'}
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
